/**
 * PhysicianCard Component
 *
 * Card displaying physician information with loading skeleton.
 * Follows the established BiteWise component patterns and integrates
 * with existing UI components (Card, TitleText, SubtitleText, BodyText).
 */

import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Card } from '../ui/Card';
import { TitleText } from '../ui/TitleText';
import { SubtitleText } from '../ui/SubtitleText';
import { BodyText } from '../ui/BodyText';
import { AvatarPhoto } from '../ui/AvatarPhoto';
import type { PhysicianCardProps } from '../../types/referralLanding';

/**
 * PhysicianCard component for displaying physician information
 *
 * @param props - PhysicianCard component props
 * @returns JSX.Element
 */
export const PhysicianCard: React.FC<PhysicianCardProps> = ({
  physician,
  isLoading = false,
  className = '',
}) => {
  const { isDark } = useTheme();

  // Loading skeleton
  if (isLoading) {
    return <PhysicianCardSkeleton className={className} />;
  }

  const cardBgColor = isDark ? 'bg-secondary-800' : 'bg-white';
  const borderColor = isDark ? 'border-secondary-700' : 'border-secondary-200';

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-6">
        {/* Header with avatar and basic info */}
        <div className="flex items-start space-x-4 mb-4">
          <AvatarPhoto
            src={physician.profilePhotoUrl}
            firstName={physician.firstName}
            lastName={physician.lastName}
            size="lg"
          />

          <div className="flex-1 min-w-0">
            <TitleText
              level={3}
              className="text-lg font-semibold mb-1 truncate"
            >
              {physician.title} {physician.firstName} {physician.lastName}
            </TitleText>

            <SubtitleText className="mb-2">
              {physician.specialty}
            </SubtitleText>

            <BodyText variant="secondary" size="sm">
              {physician.organization}
            </BodyText>
          </div>
        </div>

        {/* Contact information */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <EmailIcon className="w-4 h-4 text-secondary-500" />
            <BodyText size="sm" className="truncate">
              {physician.email}
            </BodyText>
          </div>

          {physician.phone && (
            <div className="flex items-center space-x-2">
              <PhoneIcon className="w-4 h-4 text-secondary-500" />
              <BodyText size="sm">
                {physician.phone}
              </BodyText>
            </div>
          )}
        </div>

        {/* Referring physician label */}
        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
          <div className="flex items-center space-x-2">
            <div className={`
              w-2 h-2 rounded-full
              ${isDark ? 'bg-primary-400' : 'bg-primary-500'}
            `} />
            <BodyText variant="secondary" size="sm" className="font-medium">
              Referring Physician
            </BodyText>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * PhysicianCard loading skeleton
 *
 * @param props - Skeleton props
 * @returns JSX.Element
 */
export const PhysicianCardSkeleton: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  const { isDark } = useTheme();

  const cardBgColor = isDark ? 'bg-secondary-800' : 'bg-white';
  const borderColor = isDark ? 'border-secondary-700' : 'border-secondary-200';
  const skeletonColor = isDark ? 'bg-secondary-700' : 'bg-secondary-200';

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-6 animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-start space-x-4 mb-4">
          <AvatarPhoto
            size="lg"
            loading={true}
          />

          <div className="flex-1 space-y-2">
            <div className={`h-5 ${skeletonColor} rounded w-3/4`} />
            <div className={`h-4 ${skeletonColor} rounded w-1/2`} />
            <div className={`h-3 ${skeletonColor} rounded w-2/3`} />
          </div>
        </div>

        {/* Contact skeleton */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 ${skeletonColor} rounded`} />
            <div className={`h-3 ${skeletonColor} rounded w-48`} />
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 ${skeletonColor} rounded`} />
            <div className={`h-3 ${skeletonColor} rounded w-32`} />
          </div>
        </div>

        {/* Footer skeleton */}
        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 ${skeletonColor} rounded-full`} />
            <div className={`h-3 ${skeletonColor} rounded w-24`} />
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Email icon component
 */
const EmailIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

/**
 * Phone icon component
 */
const PhoneIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

export default PhysicianCard;